import { DATA_SOURCES } from '@/config/constants';
import { getSD } from 'utils/helpers';
import { views } from '@/copy';

const { categorySummary } = views;

export const SUMMARY_SPEC = {
    [DATA_SOURCES.GOOGLE]: {
        useServerRank: true,
        siblingVolume: (s) => s.volume,
        siblingId: (s) => s.id,
        growth: (page) => page?.absoluteGrowth,
        siblingGrowth: (s) => s.absoluteGrowth,
    },
    [DATA_SOURCES.TIKTOK]: {
        useServerRank: false,
        siblingVolume: (s) => s.non_cum_avg_weekly_views,
        siblingId: (s) => s.category,
        growth: (page) => page?.non_cum_increase_YoY_rolling,
        siblingGrowth: (s) => s.non_cum_increase_YoY_rolling,
    },
    [DATA_SOURCES.INSTAGRAM]: {
        useServerRank: false,
        siblingVolume: (s) => s.non_cum_avg_weekly_likes,
        siblingId: (s) => s.category,
        growth: (page) => page?.non_cum_increase_YoY_rolling,
        siblingGrowth: (s) => s.non_cum_increase_YoY_rolling,
    },
    [DATA_SOURCES.POPULARITY]: {
        // The only source whose sibling rows nest their metrics; its caller
        // renames increase_YoY to absoluteGrowth on the current page.
        useServerRank: true,
        siblingVolume: (s) => s.data?.popularity_index_avg_12_month,
        siblingId: (s) => s.id,
        growth: (page) => page?.absoluteGrowth,
        siblingGrowth: (s) => s.data?.increase_YoY,
    },
};

export function buildCategorySummary(
    spec,
    { currentPage, parentCategory, siblings },
) {
    const result = { ...categorySummary.defaultTerms };

    result.name = currentPage.name;

    if (spec.useServerRank && currentPage?.rank) {
        result.rank = parseInt((currentPage || {}).rank, 10) || '–';
    } else {
        result.rank =
            siblings
                .sort((a, b) => spec.siblingVolume(b) - spec.siblingVolume(a))
                .findIndex(
                    (sibling) => spec.siblingId(sibling) === currentPage.id,
                ) + 1;
    }

    result.parentName = parentCategory.name || result.parentName;

    const growth = spec.growth(currentPage || {});

    if (growth < 0) {
        result.derivedGrowth = categorySummary.decliningGrowth;
    } else if (growth > 0) {
        result.derivedGrowth = categorySummary.increasingGrowth;
    } else if (growth === 0) {
        result.derivedGrowth = categorySummary.defaultGrowth;
    }

    const stData = siblings.map((sibling) => spec.siblingGrowth(sibling));
    const stDeviation = !!stData.length ? getSD(stData) : 0;
    if (Math.abs(growth) < stDeviation) {
        result.growthRate = categorySummary.lowGrowthRate;
    } else if (Math.abs(growth) > 2 * stDeviation) {
        result.growthRate = categorySummary.highGrowthRate;
    } else {
        result.growthRate = categorySummary.defaultGrowthRate;
    }

    return result;
}

/**
 * The score card's summary terms, or nothing while the three things it
 * compares - the entity, its parent and its siblings - are still arriving.
 */
export const scoreCardSummary = ({
    entity,
    parentCategory,
    siblings,
    ready,
    spec,
    currentPage,
}) => {
    const summaryReady = !!(entity && parentCategory && ready);
    return {
        summaryReady,
        summaryTerms: summaryReady
            ? buildCategorySummary(spec, {
                  currentPage,
                  parentCategory,
                  siblings,
              })
            : null,
    };
};
